import { useQuery } from "convex/react";
import { FileTextIcon, GitBranchIcon } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { formatRelativeTime } from "@/lib/time";
import { cn } from "@/lib/utils";
import type { ThreadChild, ThreadDetail } from "@/modules/threads/types";
import { api } from "../../../convex/_generated/api";

export function AgentDetailPanel({
	clericalThreadId,
}: {
	clericalThreadId: string;
}) {
	const thread = useQuery(api.agent.queries.getThread, { clericalThreadId });

	if (thread === undefined) {
		return (
			<div className="flex h-full items-center justify-center text-sm text-muted-foreground">
				Loading...
			</div>
		);
	}

	if (thread === null) {
		return (
			<div className="flex h-full items-center justify-center text-sm text-muted-foreground">
				Thread not found.
			</div>
		);
	}

	return (
		<Tabs defaultValue="agent" className="flex h-full min-h-0 flex-col gap-0">
			<div className="shrink-0 border-b px-3 py-2">
				<TabsList variant="line" className="w-full">
					<TabsTrigger value="agent">Agent</TabsTrigger>
					<TabsTrigger value="context">Context</TabsTrigger>
				</TabsList>
			</div>
			<TabsContent
				value="agent"
				className="flex min-h-0 flex-1 flex-col overflow-hidden"
			>
				<AgentTab thread={thread} />
			</TabsContent>
			<TabsContent
				value="context"
				className="flex min-h-0 flex-1 flex-col overflow-hidden"
			>
				<ContextTab thread={thread} />
			</TabsContent>
		</Tabs>
	);
}

function AgentTab({ thread }: { thread: ThreadDetail }) {
	return (
		<ScrollArea className="h-full min-h-0 flex-1">
			<div className="flex flex-col gap-4 px-5 py-4">
				<Section title="Thread">
					<dl className="flex flex-col gap-1.5 text-sm">
						<InfoRow label="ID" value={thread.clericalThreadId} mono />
						{thread.kind && <InfoRow label="Kind" value={thread.kind} />}
						{thread.statusText && (
							<InfoRow label="Status" value={thread.statusText} />
						)}
						<InfoRow
							label="Created"
							value={formatRelativeTime(thread._creationTime)}
						/>
						<InfoRow label="Convex Thread" value={thread.convexThreadId} mono />
					</dl>
				</Section>

				{thread.children.length > 0 && (
					<Section title={`Sub-threads (${thread.children.length})`}>
						<ul className="flex flex-col gap-2">
							{thread.children.map((child) => (
								<li
									key={child.clericalThreadId}
									className="flex items-start gap-2 text-sm"
								>
									<GitBranchIcon className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
									<div className="min-w-0">
										<div className="font-mono text-xs truncate">
											{child.clericalThreadId}
										</div>
										<div className="flex items-center gap-1.5 text-xs text-muted-foreground">
											{child.kind && (
												<Badge variant="outline" className="text-[10px]">
													{child.kind}
												</Badge>
											)}
											<span>{formatRelativeTime(child._creationTime)}</span>
										</div>
									</div>
								</li>
							))}
						</ul>
					</Section>
				)}

				{thread.systemPrompt && (
					<Section title="System Prompt">
						<Collapsible>
							<CollapsibleTrigger className="cursor-pointer text-xs text-muted-foreground hover:text-foreground">
								{thread.systemPrompt.length} characters — click to expand
							</CollapsibleTrigger>
							<CollapsibleContent>
								<ScrollArea className="mt-2 max-h-96 rounded-md bg-muted">
									<pre className="whitespace-pre-wrap p-3 text-xs text-muted-foreground">
										{thread.systemPrompt}
									</pre>
								</ScrollArea>
							</CollapsibleContent>
						</Collapsible>
					</Section>
				)}
			</div>
		</ScrollArea>
	);
}

function ContextTab({ thread }: { thread: ThreadDetail }) {
	return (
		<ScrollArea className="h-full min-h-0 flex-1">
			<div className="flex flex-col gap-4 px-5 py-4">
				{thread.context ? (
					<Section title="Thread Context">
						<ScrollArea className="max-h-[600px] rounded-md bg-muted">
							<pre className="whitespace-pre-wrap p-3 text-xs text-muted-foreground">
								{thread.context}
							</pre>
						</ScrollArea>
					</Section>
				) : (
					<div className="flex flex-col items-center justify-center gap-2 py-8 text-center text-sm text-muted-foreground">
						<FileTextIcon className="size-6 opacity-40" />
						<p className="font-medium text-foreground">No context attached</p>
						<p className="text-xs">
							Context is set when the agent processes an email thread or
							receives explicit context from the user.
						</p>
					</div>
				)}

				{thread.children.length > 0 && (
					<Section title="Child Thread Contexts">
						{thread.children.map((child) => (
							<ChildContextBlock key={child.clericalThreadId} child={child} />
						))}
					</Section>
				)}
			</div>
		</ScrollArea>
	);
}

function ChildContextBlock({ child }: { child: ThreadChild }) {
	if (!child.context) return null;
	return (
		<div className="mb-3">
			<div className="mb-1 flex items-center gap-1.5">
				<GitBranchIcon className="size-3 text-muted-foreground" />
				<span className="font-mono text-[11px] text-muted-foreground">
					{child.clericalThreadId}
				</span>
				{child.kind && (
					<Badge variant="outline" className="text-[10px]">
						{child.kind}
					</Badge>
				)}
			</div>
			<ScrollArea className="max-h-48 rounded-md bg-muted">
				<pre className="whitespace-pre-wrap p-2 text-xs text-muted-foreground">
					{child.context}
				</pre>
			</ScrollArea>
		</div>
	);
}

function Section({
	title,
	children,
}: {
	title: string;
	children: React.ReactNode;
}) {
	return (
		<section className="flex flex-col gap-2">
			<h4 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
				{title}
			</h4>
			<div>{children}</div>
			<Separator className="mt-2" />
		</section>
	);
}

function InfoRow({
	label,
	value,
	mono,
}: {
	label: string;
	value: string;
	mono?: boolean;
}) {
	return (
		<div className="flex items-baseline justify-between gap-3">
			<dt className="shrink-0 text-xs text-muted-foreground">{label}</dt>
			<dd className={cn("truncate text-xs", mono && "font-mono")}>{value}</dd>
		</div>
	);
}
